/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Entity } from '@backstage/catalog-model';
import { createApiRef } from '@backstage/core-plugin-api';
import {
  EntityScoreExtended,
  EntityScorePillar,
  PortfolioHistories,
  ScoreHistoryResponse,
} from './types';

export const scoringDataApiRef = createApiRef<ScoringDataApi>({
  id: 'plugin.scoringdata.service',
});

export type ScoringDataApi = {
  getScore(entity?: Entity): Promise<EntityScoreExtended | undefined>;
  getAllScores(
    entityKindFilter?: string[],
    entity?: Entity,
  ): Promise<EntityScoreExtended[] | undefined>;
  getScoreHistory(
    entity?: Entity,
    from?: string,
    to?: string,
  ): Promise<ScoreHistoryResponse | undefined>;
  /**
   * Every System's merged (IMC + radar) score history over `from`..`to`
   * (YYYY-MM-DD), keyed by lower-cased System name — the board's portfolio
   * trend (feature 0056). Rejects when IMC's bulk history is unavailable.
   */
  getPortfolioHistory(from: string, to: string): Promise<PortfolioHistories>;
  /**
   * One radar-backend pillar computed for only some of the entity's Jira
   * boards (the Delivery tab's board filter). Optional: only a client that
   * reads radar-backend has it.
   */
  getRadarPillar?(
    entity: Entity,
    pillarKey: string,
    boards: string[],
  ): Promise<EntityScorePillar | undefined>;
};
